import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import {
  createSignupAccount,
  isCurrentUserEmailVerified,
  sendSignupVerificationEmail,
  signInForSignup,
} from '../../services/auth';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';
import { SignupHeader } from './SignupLayout';
import { SignupSchoolSummary } from './SignupSchoolSummary';
import { SignupSportChoice } from './SignupSportChoice';
import { formatPrice } from './signupFormat';

/** Mirrors AcceptInviteForm and the API's own name rule (100 characters). */
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const MAX_NAME_LENGTH = 100;

type AccountFormData = {
  firstName: string;
  lastName: string;
  sport: SignupSport | undefined;
  password: string;
  confirmPassword: string;
};

export interface SignupAccountCreated {
  firstName: string;
  lastName: string;
  sport: SignupSport;
  /** The Firebase account was already verified (a resumed signup), so there is nothing to wait for. */
  alreadyVerified: boolean;
  /** The verification email could not be sent; the verify step offers Resend straight away. */
  verificationSendFailed: boolean;
}

/** What the form starts with when a coach signs back in to a signup already under way. */
export interface SignupAccountInitialValues {
  firstName: string;
  lastName: string;
  sport: SignupSport;
}

const firebaseCode = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code: unknown }).code)
    : undefined;

const createAccountMessage = (code: string | undefined): string => {
  switch (code) {
    case 'auth/weak-password':
      return 'Choose a stronger password.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/too-many-requests':
      return 'Too many attempts, try again later.';
    case 'auth/network-request-failed':
      return 'We could not reach RosterPilot. Check your connection and try again.';
    default:
      return 'We could not create your account. Please try again.';
  }
};

/**
 * Second step, for an eligible school only: the coach's name, a password, the sport, and a plain
 * statement of what the program will be billed. Creates the Firebase account and sends the
 * verification email; the RosterPilot program is not created until the address is verified.
 */
export const SignupAccountStep = ({
  email,
  lookup,
  initialValues,
  onCreated,
  onEmailInUse,
  onChangeEmail,
}: {
  email: string;
  lookup: SignupLookupResponse;
  initialValues?: SignupAccountInitialValues;
  onCreated: (created: SignupAccountCreated) => void;
  onEmailInUse: () => void;
  onChangeEmail: () => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const sports = lookup.sports;
  const initialSport =
    initialValues && sports.includes(initialValues.sport) ? initialValues.sport : undefined;

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AccountFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: initialValues?.firstName ?? '',
      lastName: initialValues?.lastName ?? '',
      // One sport is the only possible answer, so it is chosen for them.
      sport: initialSport ?? (sports.length === 1 ? sports[0] : undefined),
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');
  const price =
    lookup.annualPriceCents !== null ? formatPrice(lookup.annualPriceCents, lookup.currency) : null;

  const onSubmit = async (data: AccountFormData) => {
    // `required` on the Controller guarantees this; the guard keeps it a checked fact.
    if (!data.sport) return;

    setSubmitError('');
    setSubmitting(true);

    try {
      try {
        await createSignupAccount(email, data.password);
      } catch (error: unknown) {
        if (firebaseCode(error) !== 'auth/email-already-in-use') {
          setSubmitError(createAccountMessage(firebaseCode(error)));
          return;
        }

        // The address already has a Firebase account. When the password matches, it is this coach
        // coming back to a signup they started earlier, so pick it up rather than strand them: an
        // account with no RosterPilot program cannot sign in anywhere else. Otherwise it belongs to
        // someone who should sign in instead.
        try {
          await signInForSignup(email, data.password);
        } catch {
          onEmailInUse();
          return;
        }
      }

      const alreadyVerified = isCurrentUserEmailVerified();
      let verificationSendFailed = false;

      if (!alreadyVerified) {
        try {
          await sendSignupVerificationEmail();
        } catch {
          verificationSendFailed = true;
        }
      }

      onCreated({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        sport: data.sport,
        alreadyVerified,
        verificationSendFailed,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SignupHeader title="Create your account" subtitle={email} />

      {lookup.school && (
        <SignupSchoolSummary
          school={lookup.school}
          annualPriceCents={lookup.annualPriceCents}
          currency={lookup.currency}
        />
      )}

      <form className="accept-invite-form" onSubmit={handleSubmit(onSubmit)}>
        {submitError && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{submitError}</p>
          </div>
        )}

        <div className="accept-invite-inputs">
          <Controller
            control={control}
            name="sport"
            rules={{ required: 'Choose your sport' }}
            render={({ field }) => (
              <SignupSportChoice
                sports={sports}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.sport?.message}
                disabled={submitting}
              />
            )}
          />

          <TextInput
            label="First Name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            disabled={submitting}
            data-testid="signup-first-name"
            {...register('firstName', {
              validate: (value) => value.trim().length > 0 || 'Enter your first name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `First name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Last Name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            disabled={submitting}
            data-testid="signup-last-name"
            {...register('lastName', {
              validate: (value) => value.trim().length > 0 || 'Enter your last name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `Last name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Password"
            type="password"
            autoComplete="new-password"
            error={errors.password?.message}
            disabled={submitting}
            data-testid="signup-password"
            {...register('password', {
              required: 'Choose a password',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
              },
              maxLength: {
                value: MAX_PASSWORD_LENGTH,
                message: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            disabled={submitting}
            data-testid="signup-confirm-password"
            {...register('confirmPassword', {
              required: 'Confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
          />
        </div>

        {price && (
          <p className="signup-price-confirmation" data-testid="signup-price-confirmation">
            Your program will be billed {price} per year. No free trial.
          </p>
        )}

        <div className="accept-invite-actions">
          <Button type="submit" fullWidth loading={submitting} data-testid="signup-create-account">
            Create Account
          </Button>
          <button
            type="button"
            className="signup-text-button"
            onClick={onChangeEmail}
            disabled={submitting}
            data-testid="signup-change-email"
          >
            Use a different email
          </button>
        </div>
      </form>
    </>
  );
};
